import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import FormField from 'layouts/applications/wizard/components/FormField';
import SoftSelect from 'components/SoftSelect';
import SoftDatePicker from 'components/SoftDatePicker';
import axios from 'axios';

// Función para transformar el transporte en string a id numérico.
const convertTransporte = (transportStr) => {
  if (transportStr === "MARITIMO") return 2;
  if (transportStr === "AÉREO") return 3;
  if (transportStr === "TERRESTRE") return 1;
  return ''; // o null, según convenga
};

function OrderGeneral({
  initialData = {},
  handleFormDataChange,
  missingFields = [],
}) {
  // El valor por defecto para el cliente es el de id = 1.
  const defaultClientId = 1;

  // Para el transporte se usa la transformación en el estado inicial
  const initialTransporte =
    initialData.transporte && typeof initialData.transporte === "string"
      ? convertTransporte(initialData.transporte)
      : initialData.transporte || '';

  // Estado inicial: se usa el cliente de initialData si existe; de lo contrario se asigna defaultClientId.
  const [formData, setFormData] = useState({
    referencia: '',
    numeroOrden: '',
    origen: '',
    fechaEntrega: '',
    transporte: initialTransporte,
    cliente: initialData.cliente || defaultClientId,
    proveedor: '',
    fechaRealEntrega: '',
    incoterms: '',
    ...initialData,
  });

  // Opciones para el select de referencia
  const referenciaOptions = [
    { value: 'Factura', label: 'Factura' },
    { value: 'Numero orden', label: 'Numero orden' },
    { value: 'Packing', label: 'Packing' },
    { value: 'Tracking', label: 'Tracking' },
  ];

  // Función para resaltar campos faltantes
  const isFieldMissing = (fieldName) => missingFields.includes(fieldName);

  // Manejo de cambios en campos de texto
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedData = { ...formData, [name]: value };
    setFormData(updatedData);
    handleFormDataChange({ general: updatedData });
  };

  // Manejo de cambios en los selects.
  // Se guarda únicamente el value en formData.
  // Si se cambia el cliente, se reinicia (borra) el proveedor.
  const handleSelectChange = (name, selectedOption) => {
    const updatedData = {
      ...formData,
      [name]: selectedOption ? selectedOption.value : '',
    };
    if (name === 'cliente') {
      updatedData.proveedor = ''; // Reinicia el proveedor al cambiar el cliente
    }
    setFormData(updatedData);
    handleFormDataChange({ general: updatedData });
  };

  // Manejo de cambios en el date picker
  const handleDateChange = (name, date) => {
    const updatedData = { ...formData, [name]: date };
    setFormData(updatedData);
    handleFormDataChange({ general: updatedData });
  };

  // Actualiza formData cuando cambie initialData (asegurando que el cliente no sea nulo)
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      ...initialData,
      cliente: initialData.cliente || defaultClientId,
      transporte:
        initialData.transporte && typeof initialData.transporte === "string"
          ? convertTransporte(initialData.transporte)
          : initialData.transporte || '',
    }));
  }, [initialData]);

  // -------------------- CARGA DE DATOS --------------------

  // Clientes
  const [clientes, setClientes] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(false);
  useEffect(() => {
    const fetchClients = async () => {
      setLoadingClientes(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/maintenance/clients/`);
        // Se espera que cada cliente tenga id y nombre.
        const clientOptions = response.data.map((client) => ({
          value: client.id,
          label: client.nombre,
        }));
        setClientes(clientOptions);
      } catch (error) {
        console.error('Error al cargar los clientes:', error);
      } finally {
        setLoadingClientes(false);
      }
    };

    fetchClients();
  }, []);

  // Proveedores (solo los asociados al cliente seleccionado)
  const [proveedores, setProveedores] = useState([]);
  const [loadingProveedores, setLoadingProveedores] = useState(false);
  useEffect(() => {
    const fetchProviders = async () => {
      // Si no hay un cliente seleccionado, limpia la lista.
      if (!formData.cliente) {
        setProveedores([]);
        return;
      }
  
      setLoadingProveedores(true);
      try {
        // Usamos params para pasar los parámetros de manera más limpia
        const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/maintenance/providers/`, {
          params: {
            id_cliente: formData.cliente
          }
        });
  
        // Mapeamos los proveedores para obtener las opciones que necesitamos
        const providerOptions = response.data.map((provider) => ({
          value: provider.id_proveedor,
          label: provider.nombre,
        }));
  
        console.log(providerOptions);
        setProveedores(providerOptions);
      } catch (error) {
        console.error('Error al cargar los proveedores:', error);
        // Puedes agregar un manejo específico según el tipo de error si lo necesitas
      } finally {
        setLoadingProveedores(false);
      }
    };
  
    fetchProviders();
  }, [formData.cliente]); // Dependencia que se ejecuta cuando formData.cliente cambia  

  // Incoterms
  const [incotermsList, setIncotermsList] = useState([]);
  const [loadingIncoterms, setLoadingIncoterms] = useState(false);
  useEffect(() => {
    const fetchIncoterms = async () => {
      setLoadingIncoterms(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/maintenance/incoterms/`);
        const incotermOptions = response.data.map((item) => ({
          value: item.id_incoterm,
          label: item.nombre_incoterm,
        }));
        setIncotermsList(incotermOptions);
      } catch (error) {
        console.error('Error al cargar los incoterms:', error);
      } finally {
        setLoadingIncoterms(false);
      }
    };

    fetchIncoterms();
  }, []);

  // Transporte: variable fija con opciones
  const transportes = [
    { value: 1, label: 'TERRESTRE' },
    { value: 2, label: 'MARITIMO' },
    { value: 3, label: 'AÉREO' },
  ];

  // -------------------- RENDERIZADO --------------------
  return (
    <SoftBox>
      <SoftBox width="80%" textAlign="center" mx="auto" mb={4}>
        <SoftTypography variant="h5" fontWeight="regular">
          Comencemos agregando información general de la orden
        </SoftTypography>
      </SoftBox>
      <SoftBox mt={2}>
        <Grid container spacing={3}>
          {/* Primera columna */}
          <Grid item xs={12} md={6}>
            <SoftBox mb={2}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <SoftTypography variant="caption" fontWeight="bold" color="text">
                    Referencia
                  </SoftTypography>
                  <SoftSelect
                    options={referenciaOptions}
                    value={referenciaOptions.find(option => option.value === formData.referencia)}
                    onChange={(selectedOption) => handleSelectChange('referencia', selectedOption)}
                    placeholder="Seleccione referencia"
                    menuPortalTarget={document.body}
                    styles={{
                      control: (base) => ({
                        ...base,
                        border: isFieldMissing('referencia') ? '1px solid red' : base.border,
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormField
                    type="text"
                    label="# Número de orden"
                    name="numeroOrden"
                    value={formData.numeroOrden}
                    placeholder="Ingrese número"
                    onChange={handleInputChange}
                    style={isFieldMissing('numeroOrden') ? { border: '1px solid red' } : {}}
                  />
                </Grid>
              </Grid>
            </SoftBox>

            <SoftBox mb={2}>
              <FormField
                type="text"
                label="Origen"
                name="origen"
                value={formData.origen}
                placeholder="Ingrese origen"
                onChange={handleInputChange}
                style={isFieldMissing('origen') ? { border: '1px solid red' } : {}}
              />
            </SoftBox>

            <SoftBox mb={2}>
              <SoftTypography variant="caption" fontWeight="bold" color="text">
                Fecha de entrega
              </SoftTypography>
              <SoftDatePicker
                value={formData.fechaEntrega}
                onChange={(date) => handleDateChange('fechaEntrega', date)}
                options={{ dateFormat: 'd/m/Y' }}
                input={{ placeholder: 'Seleccione' }}
                style={
                  isFieldMissing('fechaEntrega')
                    ? { border: '1px solid red', borderRadius: '5px' }
                    : {}
                }
              />
            </SoftBox>

            <SoftBox mb={2}>
              <SoftTypography variant="caption" fontWeight="bold" color="text">
                Transporte
              </SoftTypography>
              <SoftSelect
                options={transportes}
                value={transportes.find((option) => option.value === formData.transporte)}
                onChange={(selectedOption) => handleSelectChange('transporte', selectedOption)}
                isLoading={false}
                placeholder="Seleccione el transporte"
                menuPortalTarget={document.body}
                styles={{
                  control: (base) => ({
                    ...base,
                    border: isFieldMissing('transporte') ? '1px solid red' : base.border,
                  }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
            </SoftBox>
          </Grid>

          {/* Segunda columna */}
          <Grid item xs={12} md={6}>
            <SoftBox mb={2}>
              <SoftTypography variant="caption" fontWeight="bold" color="text">
                Cliente
              </SoftTypography>
              <SoftSelect
                options={clientes}
                value={clientes.find((option) => option.value === formData.cliente)}
                onChange={(selectedOption) => handleSelectChange('cliente', selectedOption)}
                isLoading={loadingClientes}
                placeholder="Seleccione el cliente"
                menuPortalTarget={document.body}
                styles={{
                  control: (base) => ({
                    ...base,
                    border: isFieldMissing('cliente') ? '1px solid red' : base.border,
                  }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
            </SoftBox>

            {/* Se asigna un key que depende del cliente para forzar la remontada del select */}
            <SoftBox mb={2}>
              <SoftTypography variant="caption" fontWeight="bold" color="text">
                Proveedor
              </SoftTypography>
              <SoftSelect
                key={`proveedor-${formData.cliente}`}
                options={proveedores}
                value={proveedores.find((option) => option.value === formData.proveedor)}
                onChange={(selectedOption) => handleSelectChange('proveedor', selectedOption)}
                isLoading={loadingProveedores}
                placeholder="Seleccione el proveedor"
                menuPortalTarget={document.body}
                styles={{
                  control: (base) => ({
                    ...base,
                    border: isFieldMissing('proveedor') ? '1px solid red' : base.border,
                  }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
            </SoftBox>

            <SoftBox mb={2}>
              <SoftTypography variant="caption" fontWeight="bold" color="text">
                Fecha real de entrega
              </SoftTypography>
              <SoftDatePicker
                value={formData.fechaRealEntrega}
                onChange={(date) => handleDateChange('fechaRealEntrega', date)}
                options={{ dateFormat: 'd/m/Y' }}
                input={{ placeholder: 'Seleccione' }}
                style={
                  isFieldMissing('fechaRealEntrega')
                    ? { border: '1px solid red', borderRadius: '5px' }
                    : {}
                }
              />
            </SoftBox>

            <SoftBox mb={2}>
              <SoftTypography variant="caption" fontWeight="bold" color="text">
                Incoterms
              </SoftTypography>
              <SoftSelect
                options={incotermsList}
                value={incotermsList.find((option) => option.value === formData.incoterms)}
                onChange={(selectedOption) => handleSelectChange('incoterms', selectedOption)}
                isLoading={loadingIncoterms}
                placeholder="Seleccione incoterms"
                menuPortalTarget={document.body}
                styles={{
                  control: (base) => ({
                    ...base,
                    border: isFieldMissing('incoterms') ? '1px solid red' : base.border,
                  }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
            </SoftBox>
          </Grid>
        </Grid>
      </SoftBox>
    </SoftBox>
  );
}

OrderGeneral.propTypes = {
  initialData: PropTypes.object,
  handleFormDataChange: PropTypes.func.isRequired,
  missingFields: PropTypes.array,
};

export default OrderGeneral;
